import {
    Target,
    Telescope,
    ShieldCheck,
    Eye,
    BadgeCheck,
    HeartHandshake,
    Cpu,
    Zap,
    CheckCircle2,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBanner from "@/components/CtaBanner";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const VALUES = [
    { icon: ShieldCheck, title: "Security", desc: "Every exchange is handled with security and authenticated processing in mind." },
    { icon: Eye, title: "Transparency", desc: "Clear rates and a clear exchange flow — you always know what to expect." },
    { icon: BadgeCheck, title: "Reliability", desc: "A dependable exchange experience built around consistent processing." },
    { icon: HeartHandshake, title: "Customer-First", desc: "Responsive support and an experience designed around the user." },
    { icon: Cpu, title: "Technology", desc: "Modern infrastructure powering live rates and streamlined exchanges." },
    { icon: Zap, title: "Speed", desc: "A fast, focused process without unnecessary steps." },
];

const WHY_POINTS = [
    "Easy exchange experience without unnecessary complexity",
    "Competitive USDT to INR rates, fetched live",
    "Transparent process from rate to payout",
    "Secure transaction handling at every step",
    "Responsive support when you need help",
];

export default function About() {
    usePageMeta({
        title: "About DtOrbit | USDT to INR Exchange Platform",
        description:
            "Learn about DtOrbit — a digital financial technology platform making USDT to INR exchange simple, transparent and accessible.",
        path: "/about",
    });

    return (
        <>
            <PageHero
                testid="about-hero"
                label="About Us"
                title="About"
                highlight="DtOrbit"
                description="DtOrbit is a digital financial technology platform focused on making crypto-to-fiat exchange simple, transparent and accessible — starting with USDT to INR exchange."
            />

            <section data-testid="about-who-we-are" className="relative py-14 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <div className="max-w-3xl">
                            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                                Who We Are
                            </h2>
                            <p className="mt-5 text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                                DtOrbit is a financial technology platform built
                                around a simple idea: exchanging digital assets
                                for traditional currency should be easy, clear
                                and dependable. Our platform focuses on USDT to
                                INR exchange, giving users a straightforward way
                                to convert Tether (USDT) into Indian Rupees at
                                live, competitive rates.
                            </p>
                            <p className="mt-4 text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                                We combine modern technology with a
                                customer-first approach, so every exchange —
                                from checking the live rate to receiving INR —
                                feels simple and secure.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-12 grid md:grid-cols-2 gap-6">
                        <Reveal>
                            <div className="glass card-hover rounded-2xl p-7 h-full" data-testid="about-mission-card">
                                <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0] shadow-[0_8px_24px_-8px_rgba(30,86,255,0.6)]">
                                    <Target className="w-5 h-5 text-white" />
                                </span>
                                <h3 className="font-display mt-5 text-xl font-bold text-[#0F1B3D]">
                                    Our Mission
                                </h3>
                                <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed">
                                    To make digital asset exchange simple,
                                    transparent and accessible for everyone —
                                    starting with a seamless USDT to INR
                                    exchange experience.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={120}>
                            <div className="glass card-hover rounded-2xl p-7 h-full" data-testid="about-vision-card">
                                <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0] shadow-[0_8px_24px_-8px_rgba(30,86,255,0.6)]">
                                    <Telescope className="w-5 h-5 text-white" />
                                </span>
                                <h3 className="font-display mt-5 text-xl font-bold text-[#0F1B3D]">
                                    Our Vision
                                </h3>
                                <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed">
                                    To build a reliable digital financial
                                    ecosystem where moving between crypto and
                                    fiat is as easy as any everyday payment.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section data-testid="about-values" className="relative py-14 md:py-20 border-t border-[#E4EAF6] bg-white/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Our Values
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {VALUES.map(({ icon: Icon, title, desc }, i) => (
                            <Reveal key={title} delay={(i % 3) * 100}>
                                <div className="glass card-hover rounded-2xl p-6 h-full" data-testid={`about-value-${i + 1}`}>
                                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                        <Icon className="w-5 h-5 text-[#1E56FF]" />
                                    </span>
                                    <h3 className="font-display mt-4 text-base font-bold text-[#0F1B3D]">
                                        {title}
                                    </h3>
                                    <p className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                        {desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="about-why" className="relative py-14 md:py-20 border-t border-[#E4EAF6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <div className="glass rounded-2xl p-7 md:p-10">
                            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0F1B3D]">
                                Why DtOrbit
                            </h2>
                            <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                                {WHY_POINTS.map((point) => (
                                    <li key={point} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-[#1E56FF] flex-none mt-0.5" />
                                        <span className="text-sm md:text-base text-[#3D4C6E] leading-relaxed">
                                            {point}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBanner />
        </>
    );
}
